import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck, Warehouse } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { getDelhiveryConfig, updateDelhiveryConfig, registerDelhiveryWarehouse } from '@/api/delhivery';

export function DelhiveryConfigPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfgQ = useQuery({ queryKey: ['delhivery-config'], queryFn: getDelhiveryConfig });

  const [form, setForm] = useState({
    env: 'staging', clientName: '', pickupLocationName: '', pickupAddress: '',
    pickupPin: '', pickupPhone: '', sellerGstin: '', active: false, dryRun: true, ndrLive: false,
  });
  useEffect(() => {
    if (!cfgQ.data) return;
    const d = cfgQ.data;
    setForm({
      env: d.env ?? 'staging', clientName: d.clientName ?? '', pickupLocationName: d.pickupLocationName ?? '',
      pickupAddress: d.pickupAddress ?? '', pickupPin: d.pickupPin ?? '', pickupPhone: d.pickupPhone ?? '',
      sellerGstin: d.sellerGstin ?? '', active: d.active, dryRun: d.dryRun, ndrLive: d.ndrLive ?? false,
    });
  }, [cfgQ.data]);

  const set = (k: keyof typeof form, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const saveMut = useMutation({
    mutationFn: () => updateDelhiveryConfig(form),
    onSuccess: () => { toast.push('Delhivery settings saved', 'success'); qc.invalidateQueries({ queryKey: ['delhivery-config'] }); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const regMut = useMutation({
    mutationFn: registerDelhiveryWarehouse,
    onSuccess: () => { toast.push('Pickup location registered with Delhivery', 'success'); qc.invalidateQueries({ queryKey: ['delhivery-config'] }); },
    onError: (e) => toast.push(apiError(e, 'Registration failed'), 'error'),
  });

  return (
    <div>
      <SectionHeader
        title="Delhivery — Courier Configuration"        actions={<Button variant="primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>{saveMut.isPending ? 'Saving…' : 'Save Settings'}</Button>}
      />

      <div data-compact-form="true" className="space-y-2">
        <Card title="Connection">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="text-[var(--text-muted)]">API token:</span>
            <Badge tone={cfgQ.data?.tokenSet ? 'success' : 'danger'}>{cfgQ.data?.tokenSet ? 'DELHIVERY_TOKEN set' : 'Not set in env'}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="Environment" hint="Use staging until go-live">
              <Select value={form.env} onChange={(e) => set('env', e.target.value)}>
                <option value="staging">Staging</option>
                <option value="prod">Production</option>
              </Select>
            </FormGroup>
            <FormGroup label="Client Name" hint="Must match your Delhivery registration">
              <Input value={form.clientName} onChange={(e) => set('clientName', e.target.value)} placeholder="GRHAMOY" />
            </FormGroup>
            <FormGroup label="Seller GSTIN">
              <Input value={form.sellerGstin} onChange={(e) => set('sellerGstin', e.target.value)} placeholder="06AAHCG9413F1ZO" />
            </FormGroup>
          </div>
        </Card>

        <Card title="Pickup Location" subtitle="Must exactly match the warehouse name registered with Delhivery (case-sensitive).">
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="Pickup Location Name" required>
              <Input value={form.pickupLocationName} onChange={(e) => set('pickupLocationName', e.target.value)} placeholder="GRHAMOY Warehouse" />
            </FormGroup>
            <FormGroup label="Pickup Pincode" required>
              <Input value={form.pickupPin} onChange={(e) => set('pickupPin', e.target.value)} placeholder="132114" />
            </FormGroup>
            <FormGroup label="Pickup Phone" required>
              <Input value={form.pickupPhone} onChange={(e) => set('pickupPhone', e.target.value)} placeholder="8607600024" />
            </FormGroup>
            <FormGroup label="Pickup Address" className="col-span-2">
              <Input value={form.pickupAddress} onChange={(e) => set('pickupAddress', e.target.value)} placeholder="Khewat No. 168, Vill. Alipur Khalsa, Gharaunda, Karnal" />
            </FormGroup>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button variant="glass" icon={<Warehouse size={13} />} onClick={() => regMut.mutate()} disabled={regMut.isPending}>
              {regMut.isPending ? 'Registering…' : 'Register pickup location'}
            </Button>
            {cfgQ.data?.warehouseRegistered && <Badge tone="success">Registered</Badge>}
          </div>
        </Card>

        <Card title="Safety">
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
            <input type="checkbox" checked={form.dryRun} onChange={(e) => set('dryRun', e.target.checked)} className="h-4 w-4 accent-accent" />
            <span className="font-semibold">Dry-run mode</span>
            <span className="text-[11px] text-[var(--text-muted)]">No real Delhivery call — booking returns a fake AWB. Turn off to go live.</span>
          </label>
          <label className="mt-3 flex cursor-pointer select-none items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="h-4 w-4 accent-accent" />
            <span className="font-semibold">Enable tracking auto-sync</span>
            <span className="text-[11px] text-[var(--text-muted)]">Poll Delhivery every 30 min and update delivery status in the ERP.</span>
          </label>
          <label className="mt-3 flex cursor-pointer select-none items-center gap-2 text-sm">
            <input type="checkbox" checked={form.ndrLive} onChange={(e) => set('ndrLive', e.target.checked)} className="h-4 w-4 accent-accent" />
            <span className="shrink-0 whitespace-nowrap font-semibold">NDR actions live</span>
            <span className="text-[11px] text-[var(--text-muted)]">Send Re-attempt and Update details from Failed deliveries to Delhivery. Off: they are only recorded (dry run).</span>
          </label>
          {cfgQ.data?.lastPolledAt && <p className="mt-2 text-[11px] text-[var(--text-muted)]">Last tracking poll: {new Date(cfgQ.data.lastPolledAt).toLocaleString('en-IN')}</p>}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--border-soft)] bg-[var(--bg-card)] p-5">
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-extrabold text-[var(--text-primary)]"><Truck size={14} /> {title}</h3>
      {subtitle && <p className="mb-4 text-[11px] text-[var(--text-muted)]">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </section>
  );
}
